import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import safeToast from "utils/safeToast";
import { API_ENDPOINTS, getUploadUrl } from "config/api";
import { decryptText } from "config/encryption";
import axios from "axios";

// MUI Components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Avatar from "@mui/material/Avatar";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";

// Custom Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import BasicInfo from "./BasicInfo";
import ChildranDetails from "./ChildranDetails";
import EducationDetails from "./EducationDetails";
import StatutoryInfo from "./StatutaryInfo";
import AddressInfo from "./AddressInfo";
import WorkingDetails from "./WorkingDetails";
import SocialMediaLinks from "./SocialMediaLinks";
import FamilyInfo from "./FamilyInfo";
import StudentExpertise from "./StudentExpertise";
import DistrictStudentMap from "./DistrictStudentMap";
import LinkedInIntegration from "./LinkedInIntegration";
import FriendRequests from "./FriendRequests";
import geoData from '../../../API/india-districts.json';

function generateGUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

const MyDetails = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const mode = theme.palette.mode;

    const [openDrawer, setOpenDrawer] = useState(false);
    const [drawerContent, setDrawerContent] = useState(""); // <-- Added this
    const [userInfo, setUserInfo] = useState({
        name: "",
        email: "",
    });
    const [profileImageUrl, setProfileImageUrl] = useState("");
    const [profileImageId, setProfileImageId] = useState(0);
    const [imageLoading, setImageLoading] = useState(true);
    const fileInputRef = useRef(null);

    const fetchProfileImage = async (studentId, token) => {
        setImageLoading(true);
        try {
            const res = await axios.get(
                API_ENDPOINTS.PROFILE.GET_STUDENT_IMAGE(studentId),
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const imgObj = res.data && res.data.length > 0 ? res.data[0] : null;
            if (imgObj) {
                setProfileImageId(imgObj.id || 0);
                if (imgObj.imageData) {
                    // Remove '0x' if present and convert hex to base64
                    const hex = imgObj.imageData.startsWith("0x") ? imgObj.imageData.slice(2) : imgObj.imageData;
                    const byteArray = hex.match(/.{1,2}/g).map(byte => parseInt(byte, 16));
                    const uint8Array = new Uint8Array(byteArray);
                    const base64String = btoa(String.fromCharCode(...uint8Array));
                    const dataUrl = `data:${imgObj.contentType};base64,${base64String}`;
                    setProfileImageUrl(dataUrl);
                } else if (imgObj.fileName) {
                    // Use the fileName to construct the image URL
                    setProfileImageUrl(getUploadUrl(imgObj.fileName));
                } else {
                    setProfileImageUrl(""); // No image found
                }
            } else {
                setProfileImageId(0);
                setProfileImageUrl(""); // No image found
            }
        } catch (err) {
            setProfileImageId(0);
            setProfileImageUrl(""); // Error, no image
        } finally {
            setImageLoading(false);
        }
    };

    useEffect(() => {
        const sessionData = localStorage.getItem("userSession");
        if (!sessionData) {
            safeToast.warn("⚠️ No session found. Please verify your roll number.");
            navigate("/BasicAuth");
            return;
        }

        try {
            const decrypted = decryptText(sessionData);
            const parsed = JSON.parse(decrypted);
            if (!parsed?.usn || parsed.usn === "NOT -Registered") {
                safeToast.warn("⚠️ Invalid session. Redirecting...");
                navigate("/authentication/sign-up");
            } else {
                setUserInfo({
                    name: parsed?.usn || "",
                    email: parsed?.rn || "",
                });

                // Fetch student image
                const userDetails = localStorage.getItem("UserLogDetails");
                const decryptedUser = decryptText(userDetails);
                const { StudentId, Token } = JSON.parse(decryptedUser);
                fetchProfileImage(StudentId, Token);
            }
        } catch (err) {
            safeToast.error("❌ Session error. Please login again.");
            navigate("/BasicAuth");
        }
    }, [navigate]);

    const sections = [
        { title: "Basic Info", icon: "person" },
        { title: "Statutory Info", route: "/coming-soon", icon: "badge" },
        { title: "Communication", route: "/coming-soon", icon: "chat" },
        { title: "Family Info", route: "/coming-soon", icon: "family_restroom" },
        { title: "Children's Details", route: "/coming-soon", icon: "child_care" },
        { title: "Education Details", route: "/coming-soon", icon: "school" },
        { title: "Work Experience", route: "/coming-soon", icon: "work" },
        { title: "Social Media", route: "/coming-soon", icon: "public" },
        { title: "LinkedIn Integration", icon: "link" },
        { title: "Friend Requests", icon: "person_add" },
        { title: "My Expertise", route: "/coming-soon", icon: "public" },
        { title: "View My Friends", route: "/coming-soon", icon: "public" },
    ];

    const handleCardClick = (section) => {
        if (section.title === "Basic Info") {
            setDrawerContent("basic");
            setOpenDrawer(true);
        }
        else if (section.title === "Statutory Info") {
            setDrawerContent("statutory");
            setOpenDrawer(true);
        } else if (section.title === "Communication") {
            setDrawerContent("communication");
            setOpenDrawer(true);
        } else if (section.title === "Children's Details") {
            setDrawerContent("children");
            setOpenDrawer(true);
        } else if (section.title === "Education Details") {
            setDrawerContent("education");
            setOpenDrawer(true);
        }
        else if (section.title === "Work Experience") {
            setDrawerContent("work");
            setOpenDrawer(true);
        } else if (section.title === "Social Media") {
            setDrawerContent("social");
            setOpenDrawer(true);
        } else if (section.title === "Family Info") {
            setDrawerContent("family");
            setOpenDrawer(true);
        }         else if (section.title === "LinkedIn Integration") {
            setDrawerContent("linkedin");
            setOpenDrawer(true);
        } else if (section.title === "Friend Requests") {
            setDrawerContent("friendrequests");
            setOpenDrawer(true);
        } else if (section.title === "My Expertise") {
            setDrawerContent("expertise");
            setOpenDrawer(true);
        } else if (section.title === "View My Friends") {
            setDrawerContent("view My Friends");
            setOpenDrawer(true);
        } else {
            navigate(section.route);
        }
    };

    const handleImageChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Get user details
        const userDetails = localStorage.getItem("UserLogDetails");
        const decryptedUser = decryptText(userDetails);
        const { StudentId, Token } = JSON.parse(decryptedUser);

        // Prepare form data
        const formData = new FormData();
        formData.append("Id", profileImageId);
        formData.append("StudentId", StudentId);
        formData.append("File", file);

        setImageLoading(true);

        try {
            const res = await axios.post(
                API_ENDPOINTS.PROFILE.MERGE_STUDENT_IMAGE,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${Token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            if (res.data && res.data.fileUrl) {
                setProfileImageUrl(res.data.fileUrl);
            }
            // Optionally update the id if the backend returns it (not shown in your response, but you can add if needed)
            // setProfileImageId(res.data.id || profileImageId);
        } catch (err) {
            // handle error
        } finally {
            setImageLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <ToastContainer />

            <MDBox py={3}>
                {/* 👤 Modern & Compact Header */}
                <MDBox
                    position="relative"
                    textAlign="left"
                    mb={4}
                    px={isMobile ? 2 : 3}
                    py={isMobile ? 2 : 3}
                    borderRadius="md"
                    sx={{
                        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${require(
                            "assets/images/bg-sign-up-cover.jpeg"
                        )})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        color: "white",
                        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
                        borderRadius: "12px",
                    }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                style={{ display: "none" }}
                                ref={fileInputRef}
                                onChange={handleImageChange}
                            />
                            {imageLoading ? (
                                <div style={{
                                    width: isMobile ? 50 : 70,
                                    height: isMobile ? 50 : 70,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}>
                                    <span className="mdui-spinner" style={{ width: 32, height: 32 }}></span>
                                </div>
                            ) : profileImageUrl ? (
                                <img
                                    src={profileImageUrl}
                                    alt="Profile"
                                    style={{
                                        width: isMobile ? 50 : 70,
                                        height: isMobile ? 50 : 70,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        border: "2px solid white",
                                        boxShadow: "0px 2px 8px rgba(0,0,0,0.3)",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => fileInputRef.current.click()}
                                />
                            ) : (
                                <Avatar
                                    sx={{
                                        width: isMobile ? 50 : 70,
                                        height: isMobile ? 50 : 70,
                                        bgcolor: "#e0e0e0",
                                        color: "#757575",
                                        border: "2px solid white",
                                        boxShadow: "0px 2px 8px rgba(0,0,0,0.3)",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => fileInputRef.current.click()}
                                >
                                    <AddAPhotoIcon />
                                </Avatar>
                            )}
                        </Grid>
                        <Grid item xs>
                            <MDTypography
                                variant={isMobile ? "body1" : "h6"}
                                fontWeight="bold"
                                color="white"
                                mb={0.5}
                            >
                                {userInfo.name}
                            </MDTypography>
                            <MDTypography
                                variant="caption"
                                sx={{ opacity: 0.8, fontSize: isMobile ? 11 : 13 }}
                                color="white"
                            >
                                {userInfo.email}
                            </MDTypography>
                        </Grid>
                    </Grid>

                    <MDBox mt={2}>
                        <MDTypography
                            variant={isMobile ? "h6" : "h5"}
                            fontWeight="bold"
                            color="white"
                            mb={0.5}
                        >
                            My Profile Sections
                        </MDTypography>
                        <MDTypography
                            variant="body2"
                            color="white"
                            sx={{ opacity: 0.9, fontSize: isMobile ? 12 : 14 }}
                        >
                            Click below to view or update your personal and professional information.
                        </MDTypography>
                    </MDBox>
                </MDBox>

                {/* 🔽 Cards Section */}
                <Grid container spacing={isMobile ? 4 : 4}>
                    {sections.map((section, index) => (
                        <Grid item xs={6} sm={6} md={4} lg={3} key={index}>
                            <Card
                                onClick={() => handleCardClick(section)}
                                sx={{
                                    cursor: "pointer",
                                    minHeight: 170,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    backgroundColor: mode === "dark" ? "#1e1e1e" : "#fff",
                                    color: mode === "dark" ? "#f5f5f5" : "#333",
                                    boxShadow: 3,
                                    transition: "transform 0.3s, box-shadow 0.3s",
                                    "&:hover": {
                                        transform: "translateY(-3px)",
                                        boxShadow: 6,
                                    },
                                    p: 1,
                                }}
                            >
                                <MDBox
                                    variant="gradient"
                                    bgColor="info"
                                    borderRadius="lg"
                                    coloredShadow="info"
                                    mx={1}
                                    mt={-2}
                                    p={1}
                                    textAlign="center"
                                    sx={{ backgroundColor: "#1976d2" }}
                                >
                                    <MDTypography
                                        variant="body2"
                                        fontWeight="medium"
                                        sx={{
                                            fontSize: isMobile ? 12 : 13,
                                            color: "#FFFF",
                                        }}
                                    >
                                        <Tooltip title={section.title} placement="top" arrow>
                                            <Icon sx={{ verticalAlign: "middle", mr: 1, fontSize: 16 }}>
                                                {section.icon}
                                            </Icon>
                                        </Tooltip>
                                        {section.title}
                                    </MDTypography>
                                </MDBox>

                                <CardContent sx={{ p: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mb: 1,
                                            fontSize: isMobile ? 11 : 13,
                                            color: mode === "dark" ? "#f5f5f5" : "#333",
                                        }}
                                    >
                                        Access and update your {section.title.toLowerCase()} here.
                                    </Typography>

                                    <MDBox display="flex" justifyContent="center">
                                        <Tooltip title={`Click to view ${section.title}`} arrow>
                                            <MDButton
                                                variant="gradient"
                                                color="info"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCardClick(section);
                                                }}
                                                sx={{
                                                    minWidth: 36,
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: "50%",
                                                    p: 0,
                                                    boxShadow: "0px 2px 4px rgba(0,0,0,0.08)",
                                                }}
                                            >
                                                <Icon sx={{ fontSize: 18 }}>visibility</Icon>
                                            </MDButton>
                                        </Tooltip>
                                    </MDBox>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </MDBox>

            {/* Drawer for Basic Info */}
            <Drawer
                anchor="right"
                open={openDrawer}
                onClose={() => setOpenDrawer(false)}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: isMobile ? "90vw" : "480px",
                        boxSizing: "border-box",
                    },
                }}
            >
                <div style={{ width: "100%", padding: 16, position: "relative" }}>
                    {isMobile && (
                        <Tooltip title="Click to close" arrow>
                            <IconButton
                                onClick={() => setOpenDrawer(false)}
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    color: "#555",
                                    zIndex: 10,
                                }}
                                size="large"
                            >
                                <CloseIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                    )}

                    <MDTypography variant="h5" mb={2}>
                        {drawerContent === "basic" && "Basic Info"}
                        {drawerContent === "statutory" && "Statutory Info"}
                        {drawerContent === "communication" && "Communication"}
                        {drawerContent === "children" && "Children's Details"}
                        {drawerContent === "education" && "Education Details"}
                        {drawerContent === "work" && "Work Experience"}
                        {drawerContent === "social" && "Social Media Links"}
                        {drawerContent === "linkedin" && "LinkedIn Integration"}
                        {drawerContent === "family" && "Family Info"}
                        {drawerContent === "friendrequests" && "Friend Requests"}
                        {drawerContent === "expertise" && "My Expertise"}
                        {drawerContent === "view My Friends" && "View My Friends"}
                    </MDTypography>

                    {drawerContent === "basic" && <BasicInfo />}
                    {drawerContent === "statutory" && <StatutoryInfo />}
                    {drawerContent === "communication" && <AddressInfo />}
                    {drawerContent === "children" && <ChildranDetails />}
                    {drawerContent === "education" && <EducationDetails />}
                    {drawerContent === "work" && <WorkingDetails />}
                    {drawerContent === "social" && <SocialMediaLinks />}
                    {drawerContent === "linkedin" && <LinkedInIntegration />}
                    {drawerContent === "friendrequests" && <FriendRequests />}
                    {drawerContent === "family" && <FamilyInfo />}
                    {drawerContent === "expertise" && <StudentExpertise />}
                    {drawerContent === "view My Friends" && <DistrictStudentMap geoData={geoData} />}
                </div>
            </Drawer>
            <Footer />
        </DashboardLayout>
    );
};

export default MyDetails;
